/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides user-related HTTP access and application-level user lifecycle management.
 *
 * HTTP methods migrated to ApiClient (Observable-based):
 * - `loadRawUser$()`          — GET /users/me  (Pattern A, `apiClient.get()`)
 * - `loadAvailableRoles$()`   — GET /organizations/:id/roles  (Pattern A,
 *                               TTL-cached + deduplicated via `apiClient.getShared()`)
 * - `submitFeedback$()`       — POST /users/feedback  (Pattern A)
 * - `acceptDisclaimer$()`     — PATCH /organizations/:id/users/:id/disclaimer
 *                               (Pattern B — re-throws as typed Error)
 *
 * Non-HTTP methods (unchanged, remain Promise-based):
 * - `loadUser()`, `configureUser()`, `initializeUserAndRoles()`, `getToken()`,
 *   `logout()`, `handleRoles()`, `changeLanguage()`, `redirectToDisclaimerPage()`
 *
 * Deprecated Promise wrappers (backward-compat, delegate to firstValueFrom):
 * - `loadRawUser()`, `loadAvailableRoles()`, `submitFeedback()`, `acceptDisclaimer()`
 */

import {inject, Injectable} from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
} from 'rxjs';

import {User} from '../models/User';
import {environment} from '../environments/environment';
import {ApiClient} from './api-client.service';
import {RequestCacheService} from './request-cache.service';
import {AUTHENTICATION_PROVIDER} from './authentication/authentication-provider.token';
import {FeedbackDto} from '../models/feedback';
import {TranslateService} from '@ngx-translate/core';
import {UmsRole} from '../models/user-management/UserDto';
import {NgxRolesService} from 'ngx-permissions';
import {AppSettings} from '../models/Constants';
import {Role} from '../models/user-management/Role';
import {Router} from '@angular/router';
import {HttpStatusCode} from '@angular/common/http';
import {ApiError} from '../models/errors/api-error';

/** Base URL of the user-management service. */
const USER_HOST = environment.backend.user_host;

/** Cache-key prefix for the roles list. Used for targeted cache invalidation. */
const rolesUrl = (organizationId: string, filterManageable: boolean) =>
  `${USER_HOST}/organizations/${organizationId}/roles${filterManageable ? '?filterManageable=true' : ''}`;

/** Shape of the raw /users/me response envelope. */
interface MeResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly data: any;
}

/** Shape of the raw roles list response envelope. */
interface RolesResponse {
  readonly data: Role[];
}

const THIRTEEN_MINUTES_MS = 1000 * 60 * 13; //NOSONAR

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly apiClient = inject(ApiClient);
  private readonly cache = inject(RequestCacheService);
  private readonly auth = inject(AUTHENTICATION_PROVIDER);
  private readonly roleService = inject(NgxRolesService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  userDataChange: BehaviorSubject<User | null> =
    new BehaviorSubject<User | null>(null);
  userData?: User;
  availableRolesChange: BehaviorSubject<Role[] | null> = new BehaviorSubject<
    Role[] | null
  >(null);
  availableRoles: Role[] = [];
  manageableRoles: Role[] = [];

  /**
   * Emits the current organization ID whenever the authenticated user changes.
   *
   * Filters out `null` (not yet loaded) and suppresses duplicate emissions via
   * `distinctUntilChanged()` so downstream consumers only react to actual
   * organization changes.
   *
   * Intended for use in dependent services (e.g. `UserManagementService`) to
   * avoid repeating the `firstValueFrom(userDataChange)` pattern:
   *
   * ```ts
   * this.userService.orgId$.pipe(
   *   take(1),
   *   switchMap((orgId) => this.apiClient.get(url(orgId))),
   * )
   * ```
   */
  readonly orgId$: Observable<string> = this.userDataChange.pipe(
    filter((user): user is NonNullable<typeof user> => user !== null),
    map((user) => user.organization.id),
    distinctUntilChanged(),
  );

  firstTry = true;

  constructor() {
    this.initializeUserAndRoles();
  }

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Fetches the raw user profile from the `/users/me` endpoint.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @returns An `Observable` emitting the raw user data object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadRawUser$(): Observable<any> {
    return this.apiClient
      .get<MeResponse>(`${USER_HOST}/users/me`)
      .pipe(map((response) => response.data));
  }

  /**
   * Fetches the available roles for the given organization.
   *
   * The underlying GET is deduplicated and TTL-cached (5 min default) via
   * `ApiClient.getShared()`. Concurrent callers share a single in-flight request
   * and subsequent calls within the TTL window are served from cache.
   * Call `invalidateRolesCache()` after any mutation that changes the role list.
   *
   * @param organizationId   - The ID of the organization to load roles for.
   * @param filterManageable - When `true`, appends `?filterManageable=true` and
   *                           uses a separate cache entry.
   * @returns An `Observable` emitting the filtered list of `Role` objects.
   */
  loadAvailableRoles$(
    organizationId: string,
    filterManageable = false,
  ): Observable<Role[]> {
    return this.apiClient
      .getShared<RolesResponse>(rolesUrl(organizationId, filterManageable))
      .pipe(
        map((response) =>
          response.data.filter((role: Role) =>
            AppSettings.usedRoles.includes(role.label),
          ),
        ),
      );
  }

  /**
   * Submits user feedback to the backend.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param feedback - The feedback data to submit.
   * @returns An `Observable` emitting `true` on success.
   */
  submitFeedback$(feedback: FeedbackDto): Observable<true> {
    return this.apiClient
      .post<unknown>(`${USER_HOST}/users/feedback`, feedback)
      .pipe(map(() => true as const));
  }

  /**
   * Accepts the legal disclaimer for the current user.
   *
   * Re-throws a typed `Error` on failure so callers receive a meaningful
   * rejection message (Pattern B).
   *
   * @returns An `Observable` that completes on success.
   */
  acceptDisclaimer$(): Observable<void> {
    const organizationId = this.userData?.organization.id ?? '';
    const userId = this.userData?.id ?? '';
    return this.apiClient
      .patch<void>(
        `${USER_HOST}/organizations/${organizationId}/users/${userId}/disclaimer`,
        {},
      )
      .pipe(map(() => undefined as void));
  }

  // ─── Non-HTTP lifecycle methods (remain Promise-based) ───────────────────────

  async loadUser(): Promise<User> {
    try {
      const user = await this.loadRawUser();
      const selectedOrganization = user.organizations[0];
      // Add roles to role-service (handling permissions on webapp)
      this.handleRoles(selectedOrganization.roles ?? []);
      return {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        organization: selectedOrganization,
        language: user.language,
        region: user.region,
        picture: user.picture,
        status: selectedOrganization.status ?? '',
        roles: selectedOrganization.roles?.map((role: any) => role.id),
        legalDisclaimer: user.legalDisclaimer,
      };
    } catch (error: unknown) {
      if (
        error instanceof ApiError &&
        error.status === HttpStatusCode.Unauthorized &&
        this.firstTry
      ) {
        this.firstTry = false;
        window.location.href = window.location.origin + '/app'; // reload the page which implicitly gets new token with desired scopes after onboard user
        await this.getToken();
        await this.configureUser();
      }
      throw error;
    }
  }

  // ─── Deprecated Promise wrappers (backward-compat) ───────────────────────────

  /** @deprecated Use `loadRawUser$()` instead. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async loadRawUser(): Promise<any> {
    return firstValueFrom(this.loadRawUser$());
  }

  /** @deprecated Use `loadAvailableRoles$()` instead. */
  async loadAvailableRoles(
    organizationId: string,
    filterManageable = false,
  ): Promise<Role[]> {
    try {
      return await firstValueFrom(
        this.loadAvailableRoles$(organizationId, filterManageable),
      );
    } catch {
      return [];
    }
  }

  /**
   * Invalidates the cached roles list for the given organization.
   *
   * Call this after any mutation (e.g. role assignment) that changes the role
   * data so that the next `loadAvailableRoles$()` call fetches fresh data.
   *
   * @param organizationId   - The organization whose role cache should be cleared.
   * @param filterManageable - Pass `true` to also invalidate the manageable-roles
   *                           cache entry. Defaults to `false`.
   */
  invalidateRolesCache(organizationId: string, filterManageable = false): void {
    this.cache.invalidate(rolesUrl(organizationId, false));
    if (filterManageable) {
      this.cache.invalidate(rolesUrl(organizationId, true));
    }
  }

  /*
  This function adds the roles of the user to the ngx-permissions service
  It is checking the prefix like BP_SIEMENS in the roles and adding the roles without prefix
  */
  handleRoles(roles: UmsRole[]) {
    roles.forEach((userRole) => {
      AppSettings.usedRoles.forEach((availableRole) => {
        if (userRole.label.includes(availableRole)) {
          this.roleService.addRole(availableRole, []);
        }
      });
    });
  }

  initializeUserAndRoles(): void {
    this.auth.user$.subscribe(async (authUser) => {
      if (authUser?.sub) {
        await this.configureUser();
        setTimeout(async () => {
          await this.getToken();
        }, THIRTEEN_MINUTES_MS);
      }
    });
  }

  async configureUser() {
    try {
      const user = await this.loadUser();
      if (user?.organization) {
        const roles: Role[] = await this.loadAvailableRoles(
          user.organization.id,
        );
        this.availableRoles = roles.map((role) => {
          return {id: role.id, label: role.label};
        });
        this.availableRolesChange.next(this.availableRoles);
        this.userDataChange.next(user);
        this.userData = user;

        // Temporarily disabled feature 'Legal Disclaimer' for the release v1.3.0
        // The feature has been commented out because the official text is not yet available.
        // It will be re-enabled once the final content is provided.

        // if (!user.legalDisclaimer) {
        //   this.redirectToDisclaimerPage();
        // }
      }
    } catch (error: unknown) {
      if (
        error instanceof ApiError &&
        (error.status === HttpStatusCode.NotFound ||
          error.status === HttpStatusCode.Forbidden)
      ) {
        await this.logout('errorNoUser');
      } else {
        await this.logout('unknownError');
      }
    }
  }

  /**
   * Gets a fresh access token from the configured authentication provider.
   *
   * @returns A promise resolving to a valid access token.
   */
  async getToken(): Promise<string> {
    return this.auth.getAccessToken(true);
  }

  /**
   * Ends the user session and returns to the login page with an optional error fragment.
   *
   * @param pathExtension - Optional login-page fragment describing the logout reason.
   * @returns A promise that resolves after the provider accepts the logout request.
   */
  async logout(pathExtension = ''): Promise<void> {
    await this.auth.logout({returnUrl: `/login#${pathExtension}`});
  }

  /** @deprecated Use `submitFeedback$()` instead. */
  async submitFeedback(feedback: FeedbackDto): Promise<boolean> {
    return firstValueFrom(this.submitFeedback$(feedback));
  }

  /** @deprecated Use `acceptDisclaimer$()` instead. */
  async acceptDisclaimer(): Promise<void> {
    try {
      await firstValueFrom(this.acceptDisclaimer$());
    } catch {
      throw new Error('Error accepting disclaimer');
    }
  }

  // ─── Non-HTTP helpers ────────────────────────────────────────────────────────

  changeLanguage(language: string) {
    sessionStorage.setItem('language', language);
    this.translate.use(language);
  }

  redirectToDisclaimerPage() {
    const pathname = window.location.pathname;

    // redirect to the disclaimer page
    if (pathname !== '/disclaimer') {
      this.router.navigate(['/disclaimer'], {
        queryParams: {returnUrl: pathname},
      });
    }
  }
}
